import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useCart } from '../context/CartContext';

// ─── Icons (inline SVG components to avoid extra deps) ────────────────────────
const LeafIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>
  </svg>
);
const TruckIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
  </svg>
);
const UsersIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const StarIcon = ({ size = 18, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);
const ArrowRightIcon = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);
const BasketIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
  </svg>
);
const PhoneIcon = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.44 2 2 0 0 1 3.6 2.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.16 6.16l.94-.94a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.92 17z"/>
  </svg>
);
const MailIcon = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
  </svg>
);
const MapPinIcon = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);

// ─── Animated Falling Leaves Background ───────────────────────────────────────
const FallingLeaves = ({ count = 15 }) => (
  <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
    {[...Array(count)].map((_, i) => (
      <div key={i} style={{
        position: 'absolute', left: `${Math.random() * 100}%`, top: '-10%',
        animation: `v2v-falling-leaf ${8 + Math.random() * 10}s linear ${Math.random() * 5}s infinite`,
        opacity: 0.35,
      }}>
        <LeafIcon size={15 + Math.random() * 25} className={Math.random() > 0.5 ? 'color-lime' : 'color-green'} />
      </div>
    ))}
  </div>
);

// ─── Scroll Reveal ─────────────────────────────────────────────────────────────
const RevealOnScroll = ({ children, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ transition: `opacity 0.9s ease ${delay}ms, transform 0.9s ease ${delay}ms`, opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)' }}>
      {children}
    </div>
  );
};

// ─── CATEGORIES ────────────────────────────────────────────────────────────────
const CATEGORIES = ['All', 'Vegetables', 'Fruits', 'Dairy', 'Grains', 'Snacks', 'Beverages', 'Organic', 'Other'];

// ─── Product Card ──────────────────────────────────────────────────────────────
const ProductCard = ({ product }) => {
  const { addToCart, items, updateQty } = useCart();
  const navigate = useNavigate();
  const cartItem = items.find(i => i.id === product.id);

  return (
    <div className="product-card">
      <div className="img-wrap" onClick={() => navigate(`/product/${product.id}`)}>
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} />
        ) : (
          <div className="img-placeholder">🥬</div>
        )}
      </div>
      <div className="product-card-body">
        <div className="prod-cat">{product.category}</div>
        <div className="prod-name" onClick={() => navigate(`/product/${product.id}`)} style={{ cursor: 'pointer' }}>{product.name}</div>
        <div className="prod-footer">
          <div>
            <div className="prod-price">₹{parseFloat(product.price).toLocaleString('en-IN')}</div>
            {product.stock_quantity <= 10 && product.stock_quantity > 0 && (
              <div className="stock-badge low">Only {product.stock_quantity} left</div>
            )}
          </div>
          {product.status === 'out_of_stock' ? (
            <div className="out-of-stock-badge">Out of Stock</div>
          ) : cartItem ? (
            <div className="qty-control">
              <button className="qty-btn" onClick={() => updateQty(product.id, cartItem.quantity - 1)}>−</button>
              <span className="qty-value">{cartItem.quantity}</span>
              <button className="qty-btn" onClick={() => updateQty(product.id, cartItem.quantity + 1)}>+</button>
            </div>
          ) : (
            <button className="add-to-cart-btn" onClick={() => addToCart(product)}>+ Add</button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── HOME PAGE ─────────────────────────────────────────────────────────────────
const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { cartCount } = useCart();

  const fetchProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (category !== 'All') params.set('category', category);
      if (search) params.set('search', search);
      params.set('status', 'available');
      params.set('limit', '100');
      const { data } = await api.get(`/products?${params}`);
      setProducts(data.data.products);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [category, search]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const scrollToShop = () => {
    document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Stats
  const stats = [
    { label: 'Families Served', value: '3,600+' },
    { label: 'Partner Farmers', value: '500+' },
    { label: 'Chemical Free', value: '100%' },
  ];

  // How it works steps
  const steps = [
    { title: 'You Order', desc: 'Browse & add fresh items to your cart. No login needed!', emoji: '🛒' },
    { title: 'We Harvest', desc: 'Farmers harvest exactly what is needed at dawn.', emoji: '🌱' },
    { title: 'We Deliver', desc: 'Fresh produce reaches your door by evening.', emoji: '🚚' },
  ];

  // Testimonials
  const testimonials = [
    { name: 'Lakshmi R.', loc: 'Casagrand, OMR', text: 'Finally, spinach that tastes like spinach! The difference between supermarket veggies and V2V is night and day.' },
    { name: 'Rahul K.', loc: 'Prestige, ECR', text: 'I love knowing my purchase directly helps a farmer. The weekly box is a lifesaver for my busy family.' },
    { name: 'Priya S.', loc: 'Hiranandani', text: 'Quality is consistently amazing. No plastic packaging and delivered fresh right to my door. Highly recommended!' },
  ];

  return (
    <div className="v2v-landing">
      {/* ── GLOBAL STYLES ───────────────────────────────────── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;1,700&display=swap');
        .v2v-landing { font-family: 'Inter', sans-serif; }
        .v2v-landing * { box-sizing: border-box; }

        /* Animations */
        @keyframes v2v-falling-leaf {
          0%   { transform: translateY(-10%) rotate(0deg) translateX(0); opacity: 0; }
          10%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(360deg) translateX(-60px); opacity: 0; }
        }
        @keyframes v2v-blob {
          0%, 100% { transform: scale(1) translate(0, 0); }
          33%  { transform: scale(1.1) translate(30px, -50px); }
          66%  { transform: scale(0.9) translate(-20px, 20px); }
        }
        @keyframes v2v-spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes v2v-bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }
        @keyframes v2v-ping {
          75%, 100% { transform: scale(2); opacity: 0; }
        }
        @keyframes v2v-pulse-color {
          0%, 100% { opacity: 1; }  50% { opacity: 0.75; }
        }
        .v2v-blob { animation: v2v-blob 7s infinite ease-in-out; }
        .v2v-blob-delay { animation: v2v-blob 7s 2s infinite ease-in-out; }
        .v2v-spin-slow { animation: v2v-spin-slow 40s linear infinite; }
        .v2v-bounce-slow { animation: v2v-bounce-slow 3s ease-in-out infinite; }
        .v2v-ping { animation: v2v-ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite; }
        .v2v-pulse { animation: v2v-pulse-color 3s ease-in-out infinite; }
        .color-lime { color: #8cc63f; }
        .color-green { color: #4ade80; }

        /* Hero */
        .v2v-hero {
          background: linear-gradient(135deg, #fcf8e3 0%, #ffffff 50%, #f0fdf4 100%);
          min-height: 100vh; display: flex; align-items: center;
          padding: 100px 20px 60px; position: relative; overflow: hidden;
        }
        .v2v-hero-blob1 {
          position: absolute; top: 80px; left: 40px;
          width: 288px; height: 288px; border-radius: 50%;
          background: #8cc63f; filter: blur(80px); opacity: 0.15;
          mix-blend-mode: multiply; pointer-events: none;
        }
        .v2v-hero-blob2 {
          position: absolute; bottom: 40px; right: 40px;
          width: 288px; height: 288px; border-radius: 50%;
          background: #4ade80; filter: blur(80px); opacity: 0.15;
          mix-blend-mode: multiply; pointer-events: none;
        }
        .v2v-hero-sunburst {
          position: absolute; top: 0; right: 0;
          width: 700px; height: 700px; border-radius: 50%;
          background: radial-gradient(ellipse, #fef3c7 0%, transparent 70%);
          transform: translate(50%, -50%); opacity: 0.5; pointer-events: none;
        }
        .v2v-hero-inner {
          max-width: 1200px; margin: 0 auto; width: 100%;
          display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: center;
          position: relative; z-index: 2;
        }
        @media (max-width: 900px) {
          .v2v-hero-inner { grid-template-columns: 1fr; gap: 40px; }
          .v2v-hero { padding-top: 80px; min-height: auto; padding-bottom: 40px; }
          .v2v-hero-visual { display: none; }
        }

        /* Hero badge */
        .v2v-badge {
          display: inline-flex; align-items: center; gap: 10px;
          padding: 8px 20px; border-radius: 9999px;
          background: rgba(255,255,255,0.85); backdrop-filter: blur(12px);
          border: 1px solid rgba(140,198,63,0.4); font-size: 13px; font-weight: 700;
          color: #0f3d32; margin-bottom: 28px;
        }
        .v2v-badge-dot { position: relative; width: 10px; height: 10px; }
        .v2v-badge-dot-inner { width: 10px; height: 10px; background: #8cc63f; border-radius: 50%; display: block; position: relative; z-index: 1; }
        .v2v-badge-dot-ping { position: absolute; inset: 0; background: #8cc63f; border-radius: 50%; opacity: 0.75; }

        /* Hero headline */
        .v2v-headline {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(42px, 7vw, 80px);
          font-weight: 800; line-height: 1.05; color: #0f3d32;
          margin-bottom: 24px;
        }
        .v2v-headline .v2v-gradient-text {
          background: linear-gradient(90deg, #8cc63f, #4ade80);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* Hero buttons */
        .v2v-btn-primary {
          display: inline-flex; align-items: center; gap: 10px;
          padding: 18px 36px; background: #0f3d32; color: white;
          border-radius: 16px; font-size: 17px; font-weight: 700;
          border: none; cursor: pointer; transition: all 0.3s;
          font-family: inherit; text-decoration: none;
          position: relative; overflow: hidden;
        }
        .v2v-btn-primary:hover { background: #1a5c4b; transform: translateY(-2px); box-shadow: 0 20px 40px rgba(15,61,50,0.3); }
        .v2v-btn-secondary {
          display: inline-flex; align-items: center; gap: 10px;
          padding: 18px 36px; border: 2px solid rgba(15,61,50,0.15);
          color: #0f3d32; border-radius: 16px; font-size: 17px; font-weight: 700;
          background: rgba(255,255,255,0.6); backdrop-filter: blur(8px);
          cursor: pointer; transition: all 0.3s; font-family: inherit;
          text-decoration: none;
        }
        .v2v-btn-secondary:hover { background: white; border-color: #0f3d32; }

        /* Hero visual / orbit card */
        .v2v-hero-visual {
          display: flex; align-items: center; justify-content: center;
          position: relative; height: 520px;
        }
        .v2v-orbit-ring {
          position: absolute; border: 2px dashed rgba(140,198,63,0.3); border-radius: 50%;
        }
        .v2v-orbit-outer { width: 440px; height: 440px; }
        .v2v-orbit-inner { width: 320px; height: 320px; }
        .v2v-orbit-spin { animation: v2v-spin-slow 30s linear infinite; }
        .v2v-orbit-spin-reverse { animation: v2v-spin-slow 20s linear infinite reverse; }
        .v2v-orbit-icon { position: absolute; width: 52px; height: 52px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 8px 24px rgba(0,0,0,0.15); }
        .v2v-hero-card {
          background: rgba(255,255,255,0.95); backdrop-filter: blur(16px);
          border-radius: 32px; padding: 40px 48px;
          box-shadow: 0 32px 64px rgba(15,61,50,0.18);
          border: 1.5px solid rgba(255,255,255,0.9);
          text-align: center; position: relative; z-index: 10;
        }
        .v2v-hero-card-badge {
          position: absolute; top: -24px; right: -24px;
          background: #8cc63f; color: #0f3d32;
          width: 80px; height: 80px; border-radius: 50%;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          font-weight: 800; font-size: 11px; border: 4px solid white;
          box-shadow: 0 8px 20px rgba(140,198,63,0.4);
        }
        .v2v-hero-card-badge .big { font-size: 18px; line-height: 1; }

        /* Stats bar */
        .v2v-stats {
          background: linear-gradient(90deg, #0f3d32, #1a5c4b);
          padding: 48px 20px; color: white;
        }
        .v2v-stats-inner {
          max-width: 1100px; margin: 0 auto;
          display: grid; grid-template-columns: repeat(3,1fr);
          gap: 32px; text-align: center;
        }
        @media (max-width: 640px) { .v2v-stats-inner { grid-template-columns: 1fr; gap: 20px; } }
        .v2v-stat-value { font-size: 42px; font-weight: 900; color: #8cc63f; margin-bottom: 6px; }
        .v2v-stat-label { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.15em; color: rgba(255,255,255,0.7); }

        /* Features / Why Trust Us */
        .v2v-features { background: white; padding: 80px 20px; }
        .v2v-features-inner { max-width: 1200px; margin: 0 auto; }
        .v2v-section-label { font-size: 12px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase; color: #8cc63f; margin-bottom: 14px; }
        .v2v-section-title { font-family: 'Playfair Display', serif; font-size: clamp(28px, 4vw, 44px); color: #0f3d32; margin-bottom: 48px; }
        .v2v-features-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
        @media (max-width: 900px) { .v2v-features-grid { grid-template-columns: 1fr; } }
        .v2v-feature-card {
          background: white; border: 1px solid #f1f5f9; border-radius: 28px;
          padding: 40px 32px; box-shadow: 0 4px 24px rgba(0,0,0,0.07);
          transition: all 0.4s; position: relative; overflow: hidden;
        }
        .v2v-feature-card:hover { transform: translateY(-8px); box-shadow: 0 20px 48px rgba(0,0,0,0.12); }
        .v2v-feature-icon {
          width: 72px; height: 72px; background: #fcf8e3;
          border-radius: 20px; display: flex; align-items: center; justify-content: center;
          margin-bottom: 24px; color: #0f3d32; transition: transform 0.4s;
        }
        .v2v-feature-card:hover .v2v-feature-icon { transform: rotate(12deg); }
        .v2v-feature-title { font-size: 22px; font-weight: 700; color: #0f3d32; margin-bottom: 12px; font-family: 'Playfair Display', serif; }
        .v2v-feature-desc { font-size: 15px; color: #64748b; line-height: 1.7; }

        /* How it Works */
        .v2v-how { background: linear-gradient(180deg, #fcf8e3, #e6f4d0); padding: 80px 20px; }
        .v2v-how-inner { max-width: 1100px; margin: 0 auto; text-align: center; }
        .v2v-steps { display: grid; grid-template-columns: repeat(3,1fr); gap: 32px; margin-top: 48px; position: relative; }
        @media (max-width: 700px) { .v2v-steps { grid-template-columns: 1fr; gap: 24px; } }
        .v2v-step-icon {
          width: 88px; height: 88px; border-radius: 50%;
          background: white; border: 4px solid #8cc63f;
          display: flex; align-items: center; justify-content: center;
          font-size: 36px; margin: 0 auto 24px;
          box-shadow: 0 8px 24px rgba(140,198,63,0.25);
          transition: all 0.3s;
        }
        .v2v-step:hover .v2v-step-icon { background: #8cc63f; transform: scale(1.1) rotate(-12deg); }
        .v2v-step-title { font-size: 22px; font-weight: 800; color: #0f3d32; margin-bottom: 10px; }
        .v2v-step-desc { font-size: 15px; color: #4a6b63; line-height: 1.7; max-width: 260px; margin: 0 auto; }

        /* Testimonials */
        .v2v-testimonials { background: white; padding: 80px 20px; }
        .v2v-test-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; max-width: 1100px; margin: 0 auto; }
        @media (max-width: 900px) { .v2v-test-grid { grid-template-columns: 1fr; } }
        .v2v-test-card {
          background: #f8fafc; border-radius: 28px; padding: 32px;
          transition: background 0.4s; display: flex; flex-direction: column; justify-content: space-between;
        }
        .v2v-test-card:hover { background: #fcf8e3; }
        .v2v-stars { display: flex; gap: 4px; color: #f59e0b; margin-bottom: 20px; }
        .v2v-test-quote { font-size: 15px; color: #4a6b63; line-height: 1.8; font-style: italic; margin-bottom: 20px; }
        .v2v-test-author { display: flex; align-items: center; gap: 14px; }
        .v2v-test-avatar {
          width: 46px; height: 46px; border-radius: 50%;
          background: linear-gradient(135deg, #0f3d32, #1a5c4b);
          display: flex; align-items: center; justify-content: center;
          color: #8cc63f; font-weight: 800; font-size: 18px; flex-shrink: 0;
        }
        .v2v-test-name { font-size: 15px; font-weight: 700; color: #0f3d32; }
        .v2v-test-loc { font-size: 12px; color: #8cc63f; font-weight: 600; }

        /* CTA strip */
        .v2v-cta {
          background: #fcf8e3; padding: 80px 20px; text-align: center;
        }
        .v2v-cta-title { font-family: 'Playfair Display', serif; font-size: clamp(28px, 4vw, 52px); color: #0f3d32; margin-bottom: 20px; }
        .v2v-cta-sub { font-size: 17px; color: #4a6b63; max-width: 560px; margin: 0 auto 36px; }

        /* Footer redesign */
        .v2v-footer {
          background: #0f3d32; color: white; padding: 60px 20px 28px;
        }
        .v2v-footer-inner { max-width: 1100px; margin: 0 auto; }
        .v2v-footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 40px; margin-bottom: 48px; }
        @media (max-width: 700px) { .v2v-footer-grid { grid-template-columns: 1fr; gap: 28px; } }
        .v2v-footer-brand { font-size: 20px; font-weight: 800; margin-bottom: 14px; }
        .v2v-footer-desc { font-size: 14px; color: rgba(255,255,255,0.6); line-height: 1.8; }
        .v2v-footer-heading { font-size: 14px; font-weight: 700; color: #8cc63f; margin-bottom: 20px; text-transform: uppercase; letter-spacing: 0.1em; }
        .v2v-footer-contact { display: flex; flex-direction: column; gap: 16px; }
        .v2v-footer-contact-item { display: flex; align-items: center; gap: 12px; color: rgba(255,255,255,0.7); font-size: 14px; transition: color 0.2s; cursor: default; }
        .v2v-footer-contact-item:hover { color: white; }
        .v2v-footer-contact-icon { width: 38px; height: 38px; background: rgba(255,255,255,0.07); border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: background 0.2s; }
        .v2v-footer-contact-item:hover .v2v-footer-contact-icon { background: #8cc63f; color: #0f3d32; }
        .v2v-footer-bottom { border-top: 1px solid rgba(255,255,255,0.1); padding-top: 24px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; font-size: 13px; color: rgba(255,255,255,0.5); }
        .v2v-footer-bottom a { color: #8cc63f; text-decoration: none; font-weight: 700; }
        .v2v-footer-bottom a:hover { color: white; }

        /* Products section */
        .v2v-shop { background: #f8fafc; padding: 60px 20px; }
        .v2v-shop-inner { max-width: 1200px; margin: 0 auto; }
        .v2v-shop-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 28px; flex-wrap: wrap; gap: 12px; }
        .v2v-shop-title { font-family: 'Playfair Display', serif; font-size: 32px; color: #0f3d32; font-weight: 800; }
        .v2v-shop-title span { color: #8cc63f; }
        .v2v-search-bar {
          display: flex; align-items: center; gap: 10px;
          background: white; border: 2px solid #e2e8f0; border-radius: 9999px;
          padding: 12px 22px; width: 100%; max-width: 400px; margin-bottom: 24px;
          transition: border-color 0.2s;
        }
        .v2v-search-bar:focus-within { border-color: #8cc63f; }
        .v2v-search-bar input { border: none; outline: none; flex: 1; font-size: 14px; font-family: inherit; color: #0f172a; background: transparent; }
      `}</style>

      {/* ── FALLING LEAVES ─ */}
      <FallingLeaves count={18} />

      {/* ══════════════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════════════ */}
      <section className="v2v-hero" id="home">
        <div className="v2v-hero-blob1 v2v-blob" />
        <div className="v2v-hero-blob2 v2v-blob-delay" />
        <div className="v2v-hero-sunburst" />

        <div className="v2v-hero-inner">
          {/* Left: text */}
          <div>
            <RevealOnScroll>
              <div className="v2v-badge">
                <span className="v2v-badge-dot">
                  <span className="v2v-badge-dot-ping" style={{ position: 'absolute', inset: 0, borderRadius: '50%', animation: 'v2v-ping 1.5s cubic-bezier(0,0,0.2,1) infinite', background: '#8cc63f', opacity: 0.7 }} />
                  <span className="v2v-badge-dot-inner" />
                </span>
                Direct from Organic Farms
              </div>
            </RevealOnScroll>

            <RevealOnScroll delay={100}>
              <h1 className="v2v-headline">
                Honest Farming,<br />
                <span className="v2v-gradient-text v2v-pulse">Fresh Living.</span>
              </h1>
            </RevealOnScroll>

            <RevealOnScroll delay={200}>
              <p style={{ fontSize: 18, color: '#4a6b63', lineHeight: 1.8, marginBottom: 36, fontWeight: 500, maxWidth: 520 }}>
                We don't just deliver vegetables — we deliver the sunrise.
                Experience the journey from <strong style={{ color: '#8cc63f' }}>Soil to Soul</strong>.
              </p>
            </RevealOnScroll>

            <RevealOnScroll delay={300}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <button className="v2v-btn-primary" onClick={scrollToShop}>
                  <BasketIcon size={20} />
                  Shop Fresh Now
                  <ArrowRightIcon size={18} />
                </button>
                <button className="v2v-btn-secondary" onClick={() => navigate('/track')}>
                  📍 Track My Order
                </button>
              </div>
            </RevealOnScroll>
          </div>

          {/* Right: orbit visual */}
          <div className="v2v-hero-visual">
            {/* Outer orbit ring */}
            <div className="v2v-orbit-ring v2v-orbit-outer v2v-orbit-spin" style={{ position: 'absolute' }}>
              <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translate(-50%, -50%)' }}>
                <div className="v2v-orbit-icon" style={{ background: 'white', width: 52, height: 52 }}>☀️</div>
              </div>
              <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translate(-50%, 50%)' }}>
                <div className="v2v-orbit-icon" style={{ background: '#1e293b', width: 48, height: 48 }}>🌙</div>
              </div>
              <div style={{ position: 'absolute', top: '50%', right: 0, transform: 'translate(50%, -50%)' }}>
                <div className="v2v-orbit-icon" style={{ background: 'white', width: 48, height: 48 }}>🌧️</div>
              </div>
            </div>

            {/* Inner orbit ring */}
            <div className="v2v-orbit-ring v2v-orbit-inner v2v-orbit-spin-reverse" style={{ position: 'absolute', borderStyle: 'solid', borderColor: 'rgba(15,61,50,0.08)' }}>
              <div style={{ position: 'absolute', top: '50%', left: 0, transform: 'translate(-50%, -50%)' }}>
                <div className="v2v-orbit-icon" style={{ background: '#0f3d32', width: 44, height: 44 }}>
                  <TruckIcon size={22} className="color-lime" />
                </div>
              </div>
            </div>

            {/* Central card */}
            <div className="v2v-hero-card">
              <div className="v2v-hero-card-badge v2v-bounce-slow">
                <span className="big">Fresh</span>
                <span>DAILY</span>
              </div>
              <div style={{ background: 'linear-gradient(135deg,#fcf8e3,#e6f4d0)', padding: 28, borderRadius: 24, marginBottom: 20 }}>
                <BasketIcon size={64} style={{ color: '#0f3d32' }} />
              </div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: '#0f3d32', marginBottom: 10 }}>Today's Harvest</div>
              <div style={{ background: '#f8fafc', borderRadius: 9999, padding: '8px 18px', fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                Spinach · Carrots · Okra
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          STATS BAR
      ══════════════════════════════════════════════════════ */}
      <section className="v2v-stats">
        <div className="v2v-stats-inner">
          {stats.map((s, i) => (
            <RevealOnScroll key={i} delay={i * 120}>
              <div className="v2v-stat-value">{s.value}</div>
              <div className="v2v-stat-label">{s.label}</div>
            </RevealOnScroll>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          WHY TRUST US
      ══════════════════════════════════════════════════════ */}
      <section className="v2v-features">
        <div className="v2v-features-inner">
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <RevealOnScroll>
              <div className="v2v-section-label">Our Core Values</div>
              <div className="v2v-section-title">Why Your Neighbors Trust Us</div>
            </RevealOnScroll>
          </div>
          <div className="v2v-features-grid">
            {[
              { Icon: TruckIcon, title: 'Hyper-Local Delivery', desc: 'Harvested and delivered within 18 hours. We serve gated communities within our rapid logistics network.' },
              { Icon: LeafIcon, title: 'Zero-Chemical', desc: 'We practice traditional Natural Farming (Iyarkai Vivasayam). No chemicals — just nature, direct from farmers.' },
              { Icon: UsersIcon, title: 'Farmer First', desc: 'No middlemen. Your purchase increases farmer income by 30% and reduces food waste by 40%.' },
            ].map(({ Icon, title, desc }, i) => (
              <RevealOnScroll key={i} delay={i * 150}>
                <div className="v2v-feature-card">
                  <div className="v2v-feature-icon"><Icon size={34} /></div>
                  <div className="v2v-feature-title">{title}</div>
                  <div className="v2v-feature-desc">{desc}</div>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          HOW IT WORKS
      ══════════════════════════════════════════════════════ */}
      <section className="v2v-how">
        <div className="v2v-how-inner">
          <RevealOnScroll>
            <div className="v2v-section-label" style={{ textAlign: 'center' }}>Simple & Transparent</div>
            <div className="v2v-section-title" style={{ textAlign: 'center' }}>From Soil to Salad</div>
          </RevealOnScroll>
          <div className="v2v-steps">
            {steps.map((step, i) => (
              <RevealOnScroll key={i} delay={i * 180}>
                <div className="v2v-step">
                  <div className="v2v-step-icon">{step.emoji}</div>
                  <div className="v2v-step-title">{step.title}</div>
                  <div className="v2v-step-desc">{step.desc}</div>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════════════════════ */}
      <section className="v2v-testimonials">
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <RevealOnScroll>
            <div className="v2v-section-label" style={{ textAlign: 'center' }}>Community Stories</div>
            <div className="v2v-section-title" style={{ textAlign: 'center', marginBottom: 40 }}>See What Your Neighbors Are Cooking</div>
          </RevealOnScroll>
          <div className="v2v-test-grid">
            {testimonials.map((t, i) => (
              <RevealOnScroll key={i} delay={i * 120}>
                <div className="v2v-test-card">
                  <div>
                    <div className="v2v-stars">{[...Array(5)].map((_, j) => <StarIcon key={j} size={18} />)}</div>
                    <div className="v2v-test-quote">"{t.text}"</div>
                  </div>
                  <div className="v2v-test-author">
                    <div className="v2v-test-avatar">{t.name[0]}</div>
                    <div>
                      <div className="v2v-test-name">{t.name}</div>
                      <div className="v2v-test-loc">{t.loc}</div>
                    </div>
                  </div>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          CTA
      ══════════════════════════════════════════════════════ */}
      <section className="v2v-cta">
        <RevealOnScroll>
          <div style={{ fontSize: 48, marginBottom: 20 }}>🌿</div>
          <div className="v2v-cta-title">
            Ready to taste <em style={{ color: '#8cc63f' }}>real</em> vegetables?
          </div>
          <div className="v2v-cta-sub">
            Join 3,600+ families in OMR and ECR who have switched to Vivasayam to Villa Superfresh.
          </div>
          <button className="v2v-btn-primary" style={{ fontSize: 18, padding: '20px 44px', margin: '0 auto' }} onClick={scrollToShop}>
            <BasketIcon size={22} />
            Start Shopping
          </button>
        </RevealOnScroll>
      </section>

      {/* ══════════════════════════════════════════════════════
          SHOP / PRODUCTS
      ══════════════════════════════════════════════════════ */}
      <section className="v2v-shop" id="products-section">
        <div className="v2v-shop-inner">
          <div className="v2v-shop-header">
            <div className="v2v-shop-title">Fresh <span>Products</span></div>
            {cartCount > 0 && (
              <button className="btn btn-green" onClick={() => navigate('/cart')}>
                View Cart ({cartCount}) →
              </button>
            )}
          </div>

          {/* Search */}
          <div className="v2v-search-bar">
            <span>🔍</span>
            <input
              placeholder="Search vegetables, fruits..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Category filter */}
          <div className="cat-scroll" style={{ marginBottom: 28 }}>
            {CATEGORIES.map(c => (
              <button
                key={c}
                className={`cat-btn ${category === c ? 'active' : ''}`}
                onClick={() => setCategory(c)}
              >
                {c === 'All' ? '🌟 ' : c === 'Vegetables' ? '🥦 ' : c === 'Fruits' ? '🍎 ' : c === 'Dairy' ? '🥛 ' : c === 'Grains' ? '🌾 ' : ''}
                {c}
              </button>
            ))}
          </div>

          {/* Products */}
          {loading ? (
            <div className="flex-center" style={{ height: 300 }}>
              <div className="spinner" />
            </div>
          ) : products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
              <div style={{ fontSize: 48, marginBottom: 14 }}>🥬</div>
              <p style={{ fontSize: 16, fontWeight: 500 }}>No products found. Try a different category.</p>
            </div>
          ) : (
            <div className="products-grid">
              {products.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          FOOTER
      ══════════════════════════════════════════════════════ */}
      <footer className="v2v-footer">
        <div className="v2v-footer-inner">
          <div className="v2v-footer-grid">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <img src="/v.png" alt="logo" style={{ width: 52, height: 52, objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
                <div className="v2v-footer-brand">Vivasayam to Villa</div>
              </div>
              <div className="v2v-footer-desc">Bridging the gap between rural soil and urban soul. We deliver freshness, not just vegetables.</div>
            </div>
            <div>
              <div className="v2v-footer-heading">Quick Links</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {['Home', 'How It Works', 'Stories'].map(l => (
                  <a key={l} href={`#${l.toLowerCase().replace(/\s+/g, '-')}`} style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'none', fontSize: 14, transition: 'color 0.2s' }}
                    onMouseOver={e => e.target.style.color = '#8cc63f'} onMouseOut={e => e.target.style.color = 'rgba(255,255,255,0.65)'}>
                    {l}
                  </a>
                ))}
                <button onClick={() => navigate('/track')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.65)', cursor: 'pointer', fontSize: 14, textAlign: 'left', padding: 0, fontFamily: 'inherit', transition: 'color 0.2s' }}
                  onMouseOver={e => e.target.style.color = '#8cc63f'} onMouseOut={e => e.target.style.color = 'rgba(255,255,255,0.65)'}>
                  Track Order
                </button>
              </div>
            </div>
            <div>
              <div className="v2v-footer-heading">Contact Us</div>
              <div className="v2v-footer-contact">
                {[
                  { Icon: PhoneIcon, text: '+91 95859 97065' },
                  { Icon: MailIcon, text: 'contact@vivasayamtovilla.com' },
                  { Icon: MapPinIcon, text: 'Chennai, Tamil Nadu' },
                ].map(({ Icon, text }, i) => (
                  <div key={i} className="v2v-footer-contact-item">
                    <div className="v2v-footer-contact-icon"><Icon size={18} /></div>
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="v2v-footer-bottom">
            <span>© 2024 Vivasayam to Villa Superfresh. All rights reserved.</span>
            <span>Developed by <a href="https://darkpixels.in" target="_blank" rel="noopener noreferrer">Dark Pixels</a></span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
